const EVM_TX_HASH_PATTERN = /^0x[a-fA-F0-9]{64}$/;
const BARE_HEX_HASH_PATTERN = /^[a-fA-F0-9]{64}$/;
const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;
const TRON_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const BTC_BASE58_ADDRESS_PATTERN = /^(?:[13]|[mn2])[a-km-zA-HJ-NP-Z1-9]{25,89}$/;
const BTC_BECH32_ADDRESS_PATTERN = /^(bc1|tb1)[ac-hj-np-z02-9]{8,90}$/i;

function createRandomSegment() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return Math.random().toString(16).slice(2, 14);
}

function createEntityId(prefix = 'entity') {
  return `${prefix}_${Date.now()}_${createRandomSegment()}`;
}

function uniqueStrings(values = []) {
  return Array.from(new Set(values.filter(Boolean)));
}

export function normalizeTransactionHash(value = '') {
  const trimmed = String(value).trim();

  if (EVM_TX_HASH_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  if (BARE_HEX_HASH_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  return '';
}

export function normalizeAddress(value = '') {
  const trimmed = String(value).trim();

  if (EVM_ADDRESS_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  if (TRON_ADDRESS_PATTERN.test(trimmed)) {
    return trimmed;
  }

  if (BTC_BECH32_ADDRESS_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  if (BTC_BASE58_ADDRESS_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return '';
}

export function parseTransactionInput(rawInput = '') {
  const tokens = String(rawInput)
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  const valid = [];
  const invalid = [];
  const seen = new Set();

  tokens.forEach((token) => {
    const normalized = normalizeTransactionHash(token);

    if (normalized) {
      if (!seen.has(normalized)) {
        valid.push(normalized);
        seen.add(normalized);
      }
      return;
    }

    invalid.push(token);
  });

  return { valid, invalid };
}

export function parseAddressInput(rawInput = '') {
  const tokens = String(rawInput)
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  const valid = [];
  const invalid = [];
  const seen = new Set();

  tokens.forEach((token) => {
    const normalized = normalizeAddress(token);

    if (normalized) {
      if (!seen.has(normalized)) {
        valid.push(normalized);
        seen.add(normalized);
      }
      return;
    }

    invalid.push(token);
  });

  return { valid, invalid };
}

export function createStagedTransactionCandidates({
  hashes = [],
  sourceItemId = '',
  commonToAddress = '',
  notes = ''
} = {}) {
  const timestamp = new Date().toISOString();
  const rawToAddress = String(commonToAddress || '').trim();
  const normalizedToAddress = normalizeAddress(rawToAddress);
  const warnings = rawToAddress && !normalizedToAddress
    ? ['To Address format looks unusual; kept for manual review.']
    : [];

  return hashes.map((hash) => ({
    id: createEntityId('stagedTx'),
    txHash: hash,
    toAddress: normalizedToAddress || rawToAddress,
    network: '',
    validationState: 'needs_review',
    lookupStatus: 'not_attempted',
    requiresToAddress: 'unknown',
    provenanceSourceIds: sourceItemId ? [sourceItemId] : [],
    userConfirmed: false,
    notes: String(notes || '').trim(),
    warnings: [...warnings],
    createdAt: timestamp,
    updatedAt: timestamp
  }));
}

export function mergeStagedTransactions(existingTransactions = [], incomingTransactions = []) {
  const map = new Map();

  existingTransactions.forEach((transaction) => {
    map.set(transaction.txHash, {
      ...transaction,
      provenanceSourceIds: uniqueStrings(transaction.provenanceSourceIds || []),
      warnings: uniqueStrings(transaction.warnings || [])
    });
  });

  incomingTransactions.forEach((transaction) => {
    const existing = map.get(transaction.txHash);

    if (!existing) {
      map.set(transaction.txHash, {
        ...transaction,
        provenanceSourceIds: uniqueStrings(transaction.provenanceSourceIds || []),
        warnings: uniqueStrings(transaction.warnings || [])
      });
      return;
    }

    map.set(transaction.txHash, {
      ...existing,
      toAddress: existing.toAddress || transaction.toAddress || '',
      network: existing.network || transaction.network || '',
      validationState:
        existing.validationState === 'ready' || transaction.validationState === 'ready'
          ? 'ready'
          : transaction.validationState || existing.validationState || 'needs_review',
      lookupStatus: existing.lookupStatus || transaction.lookupStatus || 'not_attempted',
      requiresToAddress:
        existing.requiresToAddress !== 'unknown'
          ? existing.requiresToAddress
          : transaction.requiresToAddress,
      provenanceSourceIds: uniqueStrings([
        ...(existing.provenanceSourceIds || []),
        ...(transaction.provenanceSourceIds || [])
      ]),
      userConfirmed: Boolean(existing.userConfirmed || transaction.userConfirmed),
      notes: existing.notes || transaction.notes || '',
      warnings: uniqueStrings([...(existing.warnings || []), ...(transaction.warnings || [])]),
      updatedAt: transaction.updatedAt || new Date().toISOString()
    });
  });

  return Array.from(map.values()).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
}

export function summarizeValidationDraft(candidates = [], invalidEntries = []) {
  return {
    candidateCount: candidates.length,
    invalidCount: invalidEntries.length,
    withToAddressCount: candidates.filter((candidate) => Boolean(candidate.toAddress)).length,
    needsReviewCount: candidates.filter((candidate) => candidate.validationState === 'needs_review').length
  };
}